import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import LocaleSelector from "@/components/ui/LocaleSelector";
import { Button, Icon } from "@/components/ui";
import useUiLocale from "@/hooks/useUiLocale";
import "./download-app-ordering.css";

const APP_STORE_URL =
  "https://apps.apple.com/us/app/business-app-v26/id6526502488";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.orderingplus.storeapp";

const AndroidIcon = () => (
  <div className="ordering-platform-mark mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-[var(--od-radius-component)]">
    <Icon name="Smartphone" className="h-7 w-7" />
  </div>
);

const AppleIcon = () => (
  <div className="ordering-platform-mark ordering-platform-mark--apple mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-[var(--od-radius-component)]">
    <svg
      aria-hidden="true"
      className="h-7 w-7"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  </div>
);

const QrCard = ({ qrDataUrl, isLoading, title, description, qrAlt, icon }) => (
  <section className="ordering-download-card flex-1 p-5 sm:p-6">
    {icon}
    <h2 className="mb-1 text-center text-xl font-semibold text-[var(--od-white)]">
      {title}
    </h2>
    <p className="mb-5 text-center text-sm leading-5 text-[var(--od-ink-300)]">
      {description}
    </p>
    <div
      className="ordering-download-qr-artifact flex items-center justify-center rounded-[var(--od-radius-component)] p-3"
      aria-live="polite"
      aria-busy={isLoading || undefined}
    >
      {qrDataUrl ? (
        <img
          src={qrDataUrl}
          alt={qrAlt}
          className="w-full max-w-[240px] sm:max-w-[280px]"
        />
      ) : (
        <div className="flex aspect-square w-full max-w-[240px] items-center justify-center sm:max-w-[280px]">
          {isLoading ? (
            <div
              aria-hidden="true"
              className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--od-ink-400)] border-t-transparent motion-reduce:animate-none"
            />
          ) : (
            <span className="text-sm text-[var(--od-ink-600)]">
              {description}
            </span>
          )}
        </div>
      )}
    </div>
  </section>
);

const DownloadAppPage = () => {
  const { t } = useTranslation("downloadApp");
  const navigate = useNavigate();
  const { locale, setLocale, supportedLocales } = useUiLocale();
  const [iosQr, setIosQr] = useState("");
  const [androidQr, setAndroidQr] = useState("");
  const [isGeneratingQr, setIsGeneratingQr] = useState(true);

  const handleLocaleChange = async (event) => {
    await setLocale(event.target.value);
  };

  useEffect(() => {
    let cancelled = false;

    const generate = async () => {
      setIsGeneratingQr(true);
      try {
        const [iosDataUrl, androidDataUrl] = await Promise.all([
          QRCode.toDataURL(APP_STORE_URL, { width: 400, margin: 1 }),
          QRCode.toDataURL(PLAY_STORE_URL, { width: 400, margin: 1 }),
        ]);
        if (!cancelled) {
          setIosQr(iosDataUrl);
          setAndroidQr(androidDataUrl);
        }
      } catch {
        if (!cancelled) {
          setIosQr("");
          setAndroidQr("");
        }
      } finally {
        if (!cancelled) {
          setIsGeneratingQr(false);
        }
      }
    };

    generate();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className="ordering-download-app-surface dark flex min-h-dvh min-h-screen flex-col px-4 py-6 sm:px-6 sm:py-10 lg:px-8">
      <div className="mx-auto w-full max-w-3xl flex-1">
        {/* Top Bar */}
        <div className="mb-6 flex items-center justify-between">
          <Button
            type="button"
            emphasis="ghost"
            intent="neutral"
            size="touch"
            onClick={() => navigate("/orders")}
            className="rounded-[var(--od-radius-control)] px-2 text-[var(--od-ink-300)] hover:bg-[var(--od-ink-800)] hover:text-[var(--od-white)] focus-visible:ring-[var(--od-blue-600)] focus-visible:ring-offset-[var(--od-ink-950)]"
          >
            <Icon name="ArrowLeft" className="h-4 w-4 rtl:rotate-180" />
            {t("back")}
          </Button>
          <LocaleSelector
            id="download-locale-selector"
            label={t("language.label", { ns: "app" })}
            showLabel={false}
            showLeadingIcon={false}
            value={locale}
            onChange={handleLocaleChange}
            options={supportedLocales}
            selectClassName="h-11 min-w-[124px] border-[var(--od-ink-600)] bg-[var(--od-ink-900)] text-[var(--od-white)] focus-visible:ring-[var(--od-blue-600)] focus-visible:ring-offset-[var(--od-ink-950)]"
          />
        </div>

        {/* Header */}
        <div className="mb-10 text-center">
          <h1 className="text-3xl font-semibold tracking-[-0.025em] text-[var(--od-white)] sm:text-4xl">
            {t("title")}
          </h1>
          <p className="mt-3 text-base leading-6 text-[var(--od-ink-300)]">
            {t("subtitle")}
          </p>
        </div>

        {/* Cards */}
        <div className="flex flex-col gap-6 sm:flex-row">
          {/* Android Card */}
          <QrCard
            icon={<AndroidIcon />}
            title={t("android.title")}
            description={t("android.description")}
            qrAlt={t("android.qrAlt")}
            qrDataUrl={androidQr}
            isLoading={isGeneratingQr}
          />

          {/* iOS Card */}
          <QrCard
            icon={<AppleIcon />}
            title={t("ios.title")}
            description={t("ios.description")}
            qrAlt={t("ios.qrAlt")}
            qrDataUrl={iosQr}
            isLoading={isGeneratingQr}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="mt-12 text-center">
        <p className="text-sm text-[var(--od-ink-400)]">
          {t("footer", { year: new Date().getFullYear() })}
        </p>
      </div>
    </main>
  );
};

export default DownloadAppPage;
